// The following import still happens, but is dynamically imported in the getProducer function
// import { EventHubProducerClient } from '@azure/event-hubs'
import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';

import { dupCheck } from '@utils/funcs.ts';

const selectEventPropsToLog = R.pick(['_eventId', '_eventType', 'timestamp', 'installId']);

const CHECK_FOR_DUPES = false; // set to true to check for duplicates in the batch before sending

type AnalyticsEvent = Record<string, unknown> & { _eventType: string };

export const getCurrentTimestamp = () => new Date().toISOString();

const getCurrentAnalyticsEnv = () =>
  location.host.includes('apps.locuslabs') || location.host.includes('localhost') ? 'test' : 'prod';

export async function initEventManager({ app, webConfigId, accountId, config, log }) {
  const options = {
    oneEventPerBatch: false,
    maxBatchAge: 10 * 1000, // 10 seconds
    credentialsRefreshInterval: 30 * 60 * 1000, // 30 minutes
  };

  const noVenueIdEvents = ['appAndUserProperties', 'sessionEnd'];
  const noUserPositionEvents = ['appAndUserProperties', 'sessionEnd', 'venueLoad'];

  const state = {
    venueId: null,
    userPosition: null,
  };

  // Held until state.venueId is known; unbounded/untimed - see design doc for the buffering change.
  let pendingVenueDependentEvents: AnalyticsEvent[] = [];

  let producer = null;
  let batch = null;
  let batchCreateTimestamp = Date.now();
  let batchedEvents = [];

  const shouldSendEvents = () => !config.disableSending && !(config.disableLocalhost && app.env.isLocalhost());

  const isCryptoAvailable = () => {
    // crypto.subtle is required by Azure Event Hubs for authentication
    // It's only available in secure contexts (HTTPS or localhost)
    return typeof window !== 'undefined' && window.crypto && window.crypto.subtle !== undefined;
  };

  const getProducer = async () => {
    if (producer) {
      await producer.close();
    } // close previously opened producer
    const env = getCurrentAnalyticsEnv();
    const hubsInfoEndpoint = `https://rest.locuslabs.com/v3/azureServicebusCredentials/${accountId}?environmentName=${env}&platform=webengine`;

    return import('@azure/event-hubs')
      .then(({ EventHubProducerClient }) => {
        if (!shouldSendEvents()) {
          return null;
        }

        if (!isCryptoAvailable()) {
          log.warn(
            'crypto.subtle is not available (requires HTTPS or localhost). Event Hubs producer will not be created.',
          );
          return null;
        }

        return fetch(hubsInfoEndpoint)
          .then(res => res.json()) // https://gitlab.com/groups/locuslabs/analytics/-/wikis/Client%20side%20storage%20and%20sending%20events#https
          .then(
            eventHubsInfo => new EventHubProducerClient(eventHubsInfo.connectionString, eventHubsInfo.eventHubName),
          );
      })
      .catch(e => {
        log.error(e);
        producer = null; // de-reference these so we are clear...
        batch = null;
        if (batchedEvents.length > 0) {
          log.error(`Batch was dropped before sending events (${batchedEvents.length} events)`);
        }
        batchedEvents = [];
      });
  };

  async function newBatch() {
    batchCreateTimestamp = Date.now();
    batch = await producer.createBatch();
    batchedEvents = [];
  }

  async function sendBatch() {
    if (producer) {
      await producer.sendBatch(batch);
      batchedEvents.forEach(event => log.info('EventLifecycle [sent]:', event._eventId));
      if (CHECK_FOR_DUPES) {
        dupCheck(batchedEvents);
      }
      log.info(`Sent batch of ${batch.count} events to Azure`);
      newBatch();
    } else {
      log.warn('Attempted to send a batch but producer was null');
    }
  }

  async function refreshCredentials() {
    if (batch && batch.count) {
      await sendBatch();
    }
    if (producer) {
      await producer.close();
    }
    producer = await getProducer();
    if (producer) {
      newBatch();
    } // now that we have a new producer, we need a new batch connected to it.
  }

  const getInstallId = () => {
    const { installId } = JSON.parse(localStorage.getItem(`ll_installId_${webConfigId}`)) || {};
    if (!installId) {
      const newInstallId = uuidv4();
      localStorage.setItem(`ll_installId_${webConfigId}`, JSON.stringify({ installId: newInstallId }));
      return newInstallId;
    }
    return installId;
  };

  const removeCachedUserPosition = () => delete state.userPosition;

  const initEventQueue = []; // used to queue up events until we have a producer and batch

  /**
   * Sends analytics event to AppInsights via bus event.
   * Converts analytics2 event format to AppInsights format.
   * Uses app.bus.send() to avoid errors if monitoring plugin is not loaded.
   */
  const sendToAppInsights = event => {
    // Convert analytics2 event to AppInsights format
    // AppInsights expects: { name: string, properties: object }
    const appInsightsEvent = {
      name: `analytics2/${event._eventType}`,
      properties: {
        ...event,
        // Flatten nested objects for better querying in AppInsights
        ...(event.userPosition && {
          userPosition_lat: event.userPosition.lat,
          userPosition_lng: event.userPosition.lng,
          userPosition_floorId: event.userPosition.floorId,
          userPosition_structureId: event.userPosition.structureId,
          userPosition_ordinal: event.userPosition.ordinal,
        }),
        ...(event.fromPosition && {
          fromPosition_lat: event.fromPosition.lat,
          fromPosition_lng: event.fromPosition.lng,
          fromPosition_floorId: event.fromPosition.floorId,
          fromPosition_isUserPosition: event.fromPosition.isUserPosition,
          fromPosition_entityId: event.fromPosition.entityId,
        }),
        ...(event.toPosition && {
          toPosition_lat: event.toPosition.lat,
          toPosition_lng: event.toPosition.lng,
          toPosition_floorId: event.toPosition.floorId,
          toPosition_isUserPosition: event.toPosition.isUserPosition,
          toPosition_entityId: event.toPosition.entityId,
        }),
        ...(event.center && {
          center_lat: event.center.lat,
          center_lng: event.center.lng,
          center_ordinal: event.center.ordinal,
          center_structureId: event.center.structureId,
          center_floorId: event.center.floorId,
        }),
      },
    };

    // Use send() instead of get() to safely handle when monitoring plugin is not loaded
    // send() returns empty array if no listeners, while get() would throw an error
    app.bus.send('appInsights/log', appInsightsEvent).catch(err => {
      // Silently catch errors to avoid breaking analytics2 if AppInsights fails
      log.debug('Failed to send event to AppInsights:', err);
    });
  };

  const submitEvent = async event => {
    if (event._eventType !== 'venueLoad' && !noVenueIdEvents.includes(event._eventType) && !state.venueId) {
      // buffer-time timestamp so a later flush doesn't re-stamp the flush moment
      event.timestamp = event.timestamp || getCurrentTimestamp();
      pendingVenueDependentEvents.push(event);
      return;
    }

    if (event._eventType === 'venueLoad') {
      state.venueId = event.venueId;
    }
    if (event._eventType === 'userPosition') {
      state.userPosition = { ...event.userPosition };
    }
    event._eventId = uuidv4();
    event._eventVersion = event._eventVersion || '1';
    event.timestamp = event.timestamp || getCurrentTimestamp();
    if (noVenueIdEvents.indexOf(event._eventType) === -1 && state.venueId) {
      event.venueId = state.venueId;
    }
    if (noUserPositionEvents.indexOf(event._eventType) === -1 && state.userPosition) {
      event.userPosition = { ...state.userPosition };
    }
    event.installId = getInstallId();
    log.info(`EventLifecycle [tracked]: ${JSON.stringify(selectEventPropsToLog(event))}`);

    // Pipe the event to AppInsights monitoring
    sendToAppInsights(event);

    if (producer && batch) {
      sendEvent(event);
    } else {
      // producer is not initialized yet, so lets queue up the event until we have a producer (up to a max of 20)
      if (initEventQueue.length <= 20) {
        initEventQueue.push(event);
      }
    }

    if (event._eventType === 'venueLoad' && pendingVenueDependentEvents.length > 0) {
      // venueId is already set above, so these won't re-enter the buffering branch
      for (const bufferedEvent of pendingVenueDependentEvents) {
        await submitEvent(bufferedEvent);
      }
      pendingVenueDependentEvents = [];
    }
  };

  async function sendEvent(event) {
    if (batch) {
      const isAdded = batch.tryAdd({
        body: event,
        properties: {
          MessageType: event._eventType,
          SourceMessageVersion: '1',
          ServiceType: 'locuslabs',
          AccountId: accountId,
        },
      });
      if (isAdded) {
        batchedEvents.push(event);
        if (options.oneEventPerBatch) {
          await sendBatch();
        } else if (batch.count === 1) {
          batchCreateTimestamp = Date.now();
        } // reset batch start on first actual event
      } else {
        if (batch.count === 0) {
          // If we can't add it and the batch is empty that means the message we're trying to send
          // is too large, even when it would be the _only_ message in the batch.
          //
          // At this point you'll need to decide if you're okay with skipping this message entirely
          // or find some way to shrink it.
          log.error(`EventLifecycle [skipped]: [${event._eventId}] was too large (${JSON.stringify(event).length})`);
        } else {
          // otherwise this just signals a good spot to send our batch
          log.info('Batch is full...sending');
          await sendBatch();
          // and re-try adding this event to the new batch
          return sendEvent(event);
        }
      }
    }
  }

  function checkBatchAge() {
    if (batch) {
      log.info(`checking batch with count ${batch.count} and age ${Date.now() - batchCreateTimestamp}`);
      if (batch.count > 0 && Date.now() - batchCreateTimestamp > options.maxBatchAge) {
        sendBatch();
      }
    }
  }

  async function initEventHubs() {
    producer = await getProducer();
    if (producer) {
      await newBatch();
      log.info('EventHubs producer created - ready to batch and send events');
    }

    if (initEventQueue.length > 0) {
      initEventQueue.forEach(event => sendEvent(event));
      initEventQueue.length = 0; // clear the queue
    }

    const checkBatchAgeIntervalId = setInterval(checkBatchAge, options.maxBatchAge);
    const refreshCredentialsIntervalId = setInterval(refreshCredentials, options.credentialsRefreshInterval);

    if (typeof checkBatchAgeIntervalId.unref === 'function') {
      checkBatchAgeIntervalId.unref();
    } // Node only - do not keep-alive Lambda or other short-term environments

    if (typeof refreshCredentialsIntervalId.unref === 'function') {
      refreshCredentialsIntervalId.unref();
    } // Node only - do not keep-alive Lambda or other short-term environments
  }

  setTimeout(initEventHubs, 5000); // kick off the producer creation and batch creation asynchronously after a short delay

  return {
    getInstallId,
    submitEvent,
    removeCachedUserPosition,
  };
}
